import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { getApplication, APPLICATIONS } from "@/data/applications";
import { getMaterial } from "@/data/materials";
import { SITE } from "@/data/site";
import { Reveal, SpecChip } from "@/components/Reveal";
import NotFound from "@/pages/NotFound";

export default function ApplicationPage() {
  const { slug } = useParams();
  const app = getApplication(slug);
  if (!app) return <NotFound />;
  const related = APPLICATIONS.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{app.metaTitle}</title>
        <meta name="description" content={app.metaDescription} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: app.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: app.title,
          description: app.metaDescription,
          provider: { "@type": "Organization", name: "MESCO Springs", url: "https://www.mescosprings.com" },
          areaServed: "Worldwide",
        })}</script>
      </Helmet>

      <div className="relative pt-44 pb-20 overflow-hidden" data-testid="application-hero">
        <img src={app.image} alt={app.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FEFEFE] via-[#FEFEFE]/85 to-white/50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#706F70]">
              <Link to="/applications" className="hover:text-[#334772] transition-colors">Applications</Link>
              <span className="mx-2 text-[#334772]">/</span>{app.nav}
            </p>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase leading-[0.92] text-[#334772] mt-6 max-w-4xl">
              {app.title}
            </h1>
            <p className="mt-6 font-mono-spec text-sm tracking-[0.15em] uppercase text-[#334772]">{app.tagline}</p>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <article className="lg:col-span-8">
          {app.intro.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`text-[#706F70] leading-relaxed ${i === 0 ? "text-lg text-[#334772]" : "mt-5"}`}>{p}</p>
            </Reveal>
          ))}
          {app.sections.map((s, i) => (
            <Reveal key={s.heading} className="mt-14">
              <div className="flex items-center gap-4 mb-4">
                <span className="font-mono-spec text-[#334772] text-sm tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-px w-10 bg-[#334772]" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-[#334772]">{s.heading}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="mt-4 text-[#706F70] leading-relaxed">{p}</p>
              ))}
            </Reveal>
          ))}

          <Reveal className="mt-16">
            <h2 className="font-display text-3xl uppercase tracking-wide text-[#334772]">Frequently Asked</h2>
            <div className="mt-6 divide-y divide-[#334772]/15 border-y border-[#334772]/15">
              {app.faqs.map((f) => (
                <div key={f.q} className="py-6">
                  <h3 className="text-base font-semibold text-[#334772]">{f.q}</h3>
                  <p className="mt-2 text-sm text-[#706F70] leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </article>

        <aside className="lg:col-span-4 space-y-8">
          <Reveal>
            <div className="crosshair-card border border-[#334772]/15 bg-[#FFFFFF] p-7" data-testid="spec-table">
              <h3 className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#334772] mb-5">[ CAPABILITY SPEC ]</h3>
              <dl className="space-y-3">
                {app.specRows.map((r) => (
                  <div key={r.label} className="flex justify-between gap-4 border-b border-[#334772]/10 pb-3">
                    <dt className="font-mono-spec text-[11px] uppercase tracking-wider text-[#9C9C9C]">{r.label}</dt>
                    <dd className="font-mono-spec text-[11px] text-[#334772] text-right">{r.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="border border-[#334772]/15 bg-[#FFFFFF] p-7">
              <h3 className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#334772] mb-5">[ SPRINGS WE SUPPLY ]</h3>
              <ul className="space-y-2.5">
                {app.springTypes.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-[#706F70]">
                    <Check className="w-4 h-4 text-[#334772] mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-[#334772]/15 bg-[#FFFFFF] p-7">
              <h3 className="font-mono-spec text-[11px] tracking-[0.3em] uppercase text-[#334772] mb-5">[ RECOMMENDED MATERIALS ]</h3>
              <div className="flex flex-wrap gap-2">
                {app.materials.map((ms) => {
                  const m = getMaterial(ms);
                  return m ? (
                    <Link key={ms} to={`/materials/${ms}`} data-testid={`app-material-link-${ms}`}>
                      <span className="inline-block font-mono-spec text-[10px] tracking-[0.15em] uppercase text-[#706F70] border border-[#334772]/25 px-3 py-1.5 hover:border-[#334772] hover:text-[#334772] transition-colors duration-200">
                        {m.name}
                      </span>
                    </Link>
                  ) : null;
                })}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-[#334772]/40 bg-[#334772]/5 p-7">
              <h3 className="font-display text-2xl uppercase tracking-wide text-[#334772]">Specify this spring</h3>
              <p className="mt-3 text-sm text-[#706F70] leading-relaxed">Send your drawing, sample or duty data — RFQs answered within 24 hours.</p>
              <div className="mt-5 flex flex-col gap-3">
                <Link to="/enquiry" data-testid="app-rfq-btn" className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#334772] text-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#2A3C60] transition-colors">
                  Send an RFQ <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
                <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" data-testid="app-mainsite-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-[#334772]/40 text-[#334772] text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#334772] hover:text-white transition-colors">
                  mescosprings.com <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </Reveal>
        </aside>
      </div>

      <div className="border-t border-[#334772]/15 bg-[#F3F5F8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <Reveal>
            <h2 className="font-display text-3xl uppercase tracking-wide text-[#334772] mb-8">Related applications</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.08}>
                <Link to={`/applications/${r.slug}`} data-testid={`related-app-${r.slug}`} className="group block border border-[#334772]/15 p-6 hover:border-[#334772] hover:-translate-y-1 transition-all duration-300 bg-[#F3F5F8]">
                  <span className="font-mono-spec text-[10px] tracking-[0.25em] uppercase text-[#9C9C9C]">Application</span>
                  <h3 className="font-display text-2xl text-[#334772] uppercase tracking-wide mt-2 group-hover:text-[#706F70] transition-colors">{r.nav}</h3>
                  <p className="mt-2 text-sm text-[#706F70]">{r.tagline}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
